import Card from '../Card/Card';
import './MemberCard.css';
import ChevronLink from '../ChevronLink/ChevronLink';
import { useCouncilPath } from '../../../hooks/useCouncilPath';
import MaleSvgAvatar from './MaleAvatarSvg';
import FemaleAvatarSvg from './FemaleAvatarSvg';
import { useTranslation, Trans } from 'react-i18next';
import { capitalizeFirstLetter } from '../../../utils/texts';
import { Tooltip } from '../../PrimereactComponents';

type ActiveMemberData = {
    id: number;
    firstName: string;
    lastName: string;
    gender: 0 | 1;
    inactive?: boolean;
    customCardContent?: React.ReactNode;
    photo?: string;
};

type InactiveMemberData = ActiveMemberData & {
    inactive: boolean;
    newPartyData?: PartyData | null;
};

type LeaderMemberData = ActiveMemberData & {
    function: string;
};

export type MemberDataType = ActiveMemberData | InactiveMemberData | LeaderMemberData;

type PartyData = {
    partyName: string;
    partyColor: string;
    isNowFor?: boolean;
};

type Props = {
    className?: string;
    partyData: PartyData;
    memberData: MemberDataType;
    customCardContent?: React.ReactNode;
    leadershipRole?: string;
};

const MemberCard = ({ className, partyData, memberData, customCardContent, leadershipRole }: Props) => {
    const councilPath = useCouncilPath();
    const { t } = useTranslation(['members', 'translation']);

    const photo = memberData.photo ?? null;

    return (
        <Card
            className='member-card'
            elevation={1}
            link={councilPath('zastupitele', memberData.id)}
        >
            <div className='member-avatar'>
            {photo ? (
                <img
                src={`${memberData.photo}`}
                className='member-photo'
                style={{ borderColor: partyData.partyColor }}
                />
            ) : (
                memberData?.gender === 0 ? (
                <MaleSvgAvatar color={partyData.partyColor} />
                ) : (
                <FemaleAvatarSvg color={partyData.partyColor} />
                )
            )}
            </div>
            <div className='member-info'>
                <div className='member-card-content'>
                    <p className='member-name'>
                        {memberData.firstName} {memberData.lastName}
                    </p>
                    {leadershipRole && (
                        <>
                            <span
                                id={`leadership-crown-${memberData.id}`}
                                className='material-symbols-rounded leadership-crown'
                                style={{ color: partyData.partyColor }}
                            >
                                crown
                            </span>
                            <Tooltip target={`#leadership-crown-${memberData.id}`}>{leadershipRole}</Tooltip>
                        </>
                    )}
                </div>
                <div className='member-card-content' style={customCardContent ? { alignItems: 'center' } : {}}>
                    {customCardContent ? (
                        customCardContent
                    ) : (
                        <>
                        {"function" in memberData && memberData.function ? (
                            <p>
                                <Trans
                                    ns="members"
                                    i18nKey="MemberCard.forParty"
                                    values={{ function: memberData.function, partyName: partyData.partyName }}
                                    components={{
                                        party: <span style={{ color: partyData.partyColor }} />
                                    }}
                                />
                            </p>
                        ) : partyData.isNowFor ? (
                            <p>
                                <Trans
                                    ns="members"
                                    i18nKey="MemberCard.nowForParty"
                                    values={{ partyName: partyData.partyName }}
                                    components={{
                                        party: <span style={{ color: partyData.partyColor }} />
                                    }}
                                />
                            </p>
                        ) : (
                            <p style={{ color: partyData.partyColor }}>{partyData.partyName}</p>
                        )}
                        </>
                    )}
                    <ChevronLink 
                        chevronDirection='right'
                        text={t('details', { ns: 'translation' })}
                        linkDisabled />
                </div>
            </div>
        </Card>
    );
};

export default MemberCard;
